import type { ShapeKind, ShapeParams } from '../types.ts'

/**
 * Design-Dokument v1. Alle Längen in mm. Ursprung = linke obere Ecke des Endformats
 * (Trim-Box); der Beschnitt liegt bei negativen Koordinaten. Reihenfolge im `layers`-Array
 * = Z-Reihenfolge (erstes Element hinten). Referenzen statt Kopien (uploadId, assetId,
 * familyId); `url/width/height` sind denormalisierte Render-Hilfen.
 */
export const DESIGN_SCHEMA_VERSION = 1 as const

export type CanvasZone = {
  kind: 'hidden' | 'handle' | 'seam'
  xMm: number
  yMm: number
  wMm: number
  hMm: number
  label?: string
}

export type CanvasBackground =
  | { type: 'color'; value: string }
  | { type: 'asset'; assetId: string; url?: string; tint?: string | null }
  | { type: 'upload'; uploadId: string; url?: string; width?: number; height?: number; fit?: ImageFit }
  | { type: 'gradient'; angleDeg: number; stops: Array<{ offset: number; color: string }> }

export type DesignCanvas = {
  widthMm: number
  heightMm: number
  bleedMm: number
  safeMarginMm: number
  dpi: number
  shape: ShapeKind
  cornerRadiusMm: number
  shapeParams?: ShapeParams | null
  zones?: CanvasZone[]
  background: CanvasBackground
  colorProfile?: 'sRGB'
}

export type Frame = { xMm: number; yMm: number; wMm: number; hMm: number; rotationDeg: number }

/** Regeln aus Vorlagen: was der Kunde an diesem Layer ändern darf. */
export type LayerConstraints = {
  locked: boolean
  contentOnly: boolean
  optional: boolean
  role?: 'slot' | 'full-bleed' | 'decor' | 'address-lines' | 'stamp' | 'greeting' | string
}

export type ImageFit = {
  mode: 'cover' | 'contain'
  offsetX: number
  offsetY: number
  scale: number
  flipH?: boolean
  flipV?: boolean
}

export type ImageSource =
  | { kind: 'upload'; uploadId: string; url?: string; width?: number; height?: number }
  | { kind: 'asset'; assetId: string; url?: string; width?: number; height?: number }
  | { kind: 'placeholder'; slotIndex?: number }

export type LayerMask =
  | { kind: 'none' }
  | { kind: 'circle' }
  | { kind: 'rounded'; radiusMm: number }
  | { kind: 'hexagon'; orientation?: 'flat' | 'pointy' }
  | { kind: 'asset'; assetId: string; path?: string }
  | { kind: 'svg'; path: string }

export type ImageFilters = {
  grayscale: boolean
  sepia: number
  brightness: number
  contrast: number
  saturation: number
}

export type Border = { widthMm: number; color: string } | null
export type Shadow = { xMm: number; yMm: number; blurMm: number; color: string } | null
export type Stroke = { widthMm: number; color: string } | null

export type LayerBase = {
  id: string
  name?: string
  visible: boolean
  locked: boolean
  opacity: number
  frame: Frame
  constraints: LayerConstraints
}

export type ImageLayer = LayerBase & {
  type: 'image'
  source: ImageSource
  fit: ImageFit
  mask: LayerMask
  filters: ImageFilters
  border: Border
  shadow: Shadow
}

export type TextFont = {
  familyId: string
  /** CSS-Familienname (aus FontFamily.name), fällt auf familyId zurück */
  family?: string
  weight: number
  italic: boolean
  sizePt: number
  lineHeight: number
  letterSpacingEm: number
}

export type TextLayer = LayerBase & {
  type: 'text'
  text: string
  font: TextFont
  color: string
  align: 'left' | 'center' | 'right' | 'justify'
  verticalAlign: 'top' | 'middle' | 'bottom'
  autoFit: 'none' | 'shrink'
  /** Vom Client gemessene Größe bei autoFit=shrink; der Druck misst nie nach. */
  fittedSizePt?: number
  stroke: Stroke
  shadow: Shadow
}

export type StickerLayer = LayerBase & {
  type: 'sticker'
  source: { assetId: string; url?: string; width?: number; height?: number }
  tint: string | null
  flipH?: boolean
  flipV?: boolean
}

export type ShapeLayer = LayerBase & {
  type: 'shape'
  shape: 'rect' | 'ellipse' | 'line' | 'polygon'
  sides?: number
  fill: string
  stroke: Stroke
  cornerRadiusMm: number
  dashed?: boolean
}

export type Layer = ImageLayer | TextLayer | StickerLayer | ShapeLayer
export type LayerType = Layer['type']

/** Bogen (Fotosticker, Passbilder, Fotosets): feste Fotoplätze, der Kunde tauscht nur Fotos. */
export type SheetMeta = {
  templateId: string
  name?: string
  /** Kennung des Altsystem-Layouts (z. B. „138 - Fotoset 38“) */
  legacyName?: string | null
}

export type DesignMeta = {
  templateId?: string | null
  templateVersion?: number | null
  generator?: string
  /** 'sheet' = Bogen-Vorlage (nur in Vorlagen-Dokumenten) */
  kind?: 'design' | 'sheet'
  /** Kundendokument basiert auf diesem Bogen */
  sheet?: SheetMeta | null
  /** Produktionsmarken des Altsystems (Auftragsnummer, QR), nur für den Bogen-Render */
  productionMarks?: Array<Record<string, unknown>>
}

export type DesignDocument = {
  schemaVersion: typeof DESIGN_SCHEMA_VERSION
  canvas: DesignCanvas
  layers: Layer[]
  meta: DesignMeta
}

/** Font-Deskriptor für @font-face (vom Backend: FontFamily + FontFace). */
export type FontDescriptor = {
  familyId: string
  family: string
  weight: number
  italic: boolean
  woff2Url: string
  ttfUrl?: string
}
